import React, { useMemo } from 'react';
import styled from 'styled-components';
import { applyFilterAreaHeight } from '../constants';
import Button from '@prisjakt/ui-button';
import CrossIcon from 'mdi-react/CloseIcon';
import { ListHeader } from 'src/components/list-header/list-header';
import { layout } from '@prisjakt/ui-base';
import { GridRow } from '@prisjakt/ui/list/variants';
import { SlideLeft, SlideRight } from '@prisjakt/ui/hideable/variants';
import { Speed, Delay, Direction } from '@prisjakt/ui';
import NoScroll from 'src/components/no-scroll';
import Overlay from 'src/components/overlay';
import { zindex } from 'src/ui/zindex';

type OverlayWrapperProps = {
  applyButton?: React.ReactNode;
  children: React.ReactNode;
  onBack?: () => void;
  onClose: () => void;
  showBackArrow?: boolean;
  title: string;
};

function OverlayWrapper({
  applyButton,
  children,
  onBack,
  onClose,
  showBackArrow = false,
  title,
}: OverlayWrapperProps) {
  const crossIcon = useMemo(() => <CrossIcon />, []);

  return (
    <>
      <NoScroll mobile tablet />
      <Overlay active />
      <Dialog>
        <Header>
          <ListHeader
            title={title}
            onClick={onBack}
            showBackArrow={showBackArrow}
          >
            <Button
              color="quarternary"
              variant="icon"
              onClick={onClose}
              icon={crossIcon}
            />
          </ListHeader>
        </Header>

        {children}
        {applyButton || null}
      </Dialog>
    </>
  );
}

type OverlayViewProps = {
  children: React.ReactNode;
  slideDirection: Direction.Left | Direction.Right;
  visible: boolean;
};

function OverlayView({ children, slideDirection, visible }: OverlayViewProps) {
  const Hideable = {
    [Direction.Left]: SlideLeft,
    [Direction.Right]: SlideRight,
  }[slideDirection];
  return (
    <Hideable visible={visible} speed={Speed.Fast} delayIn={Delay.Short}>
      <GridRow gap={parseFloat(layout.measurements.spacing100)}>
        {children}
      </GridRow>
    </Hideable>
  );
}

export { OverlayWrapper, OverlayView };

export const Dialog = styled.div`
  min-width: 0;
  flex: 1 1 0;
  background: ${p => p.theme.bg.primary};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zindex.menu.filter};
  overflow-x: hidden;
  overflow-y: scroll; /* has to be scroll, not auto */
  -webkit-overflow-scrolling: touch;
  /* Makes sure no content is hidden behind the "apply filter" area */
  ::after {
    content: '';
    display: block;
    height: ${applyFilterAreaHeight}px;
  }
`;

export const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;
